import path from 'path';
import { SDM } from '@siliconlabs/sdm-schema';
import { logger } from '../services/logger';

/**
 * Exhaustive check function for component switch statements. If we
 * get inside this function, it means the SDM contains an element
 * not handled by the renderer (i.e., there is a version mismatch
 * between SDM schema and renderer).
 *
 * @param type of SDM node
 */
function _exhaustiveCheck(type: never) {
  logger.error(
    `Unhandled Element: ${JSON.stringify(type)}. Please verify SDM and SUDS binary are up-to-date.`,
  );
  return null;
}

const ALLOWED_STYLES = {
  table: [
    'invisible-borders',
    'valign-bottom',
    'valign-top',
    'valign-middle',
    'table-cell-vertical',
    'classic-theme',
  ],
  td: [
    'shaded',
    'valign-bottom',
    'valign-top',
    'valign-middle',
    'table-cell-vertical',
    'align-center',
    'align-left',
    'align-right',
  ],
  th: [
    'shaded',
    'valign-bottom',
    'valign-top',
    'valign-middle',
    'table-cell-vertical',
    'align-center',
    'align-left',
    'align-right',
  ],
};

type AllowedStyles = keyof typeof ALLOWED_STYLES;

/**
 * Utility function to determine component styling based on SDM props
 */
function resolveStyle(tag: AllowedStyles, style: string | undefined) {
  if (!style) {
    return '';
  }
  const classes = style.split(',');
  let allowClasses: string[] = [];
  if (tag in ALLOWED_STYLES) {
    allowClasses = classes.filter((e) => ALLOWED_STYLES[tag].includes(e));
  }
  return allowClasses.join(' ');
}

function resolveInlineStyle(
  tag: AllowedStyles,
  widthStyle: any,
  color?: string,
) {
  const inlineStyle: { [style: string]: string } = {};

  if (color) {
    inlineStyle.color = color;
  }

  if (!ALLOWED_STYLES[tag] || !widthStyle) {
    return inlineStyle;
  }

  if (tag === 'table') {
    inlineStyle.display = 'inline-table';
    return inlineStyle;
  }

  if (widthStyle.width) {
    inlineStyle.width = widthStyle.width;
  }

  if (widthStyle.minWidth) {
    inlineStyle.minWidth = widthStyle.minWidth;
  }

  return inlineStyle;
}

function mapCellWidth(rows: SDM.Content.TableRow[], width?: string) {
  if (!width) {
    return [];
  }

  const widths = width.split(',');
  const toSpanNumber = (str?: number) => (Number(str) ? Number(str) : 1);
  const cols =
    rows[0]?.children?.reduce((s, val) => {
      s += toSpanNumber(val.attributes?.colspan);
      return s;
    }, 0) ?? 0;
  const widthArray = new Array(rows.length)
    .fill(null)
    .map(() => new Array(cols).fill(undefined));

  rows.forEach((row, i) => {
    let currentIndex = 0;
    while (widthArray[i][currentIndex] === 'occupied') {
      currentIndex++;
    }
    row.children?.forEach((c) => {
      const regex = /^(\d*)(px|%|vw|vh|rem|em|vmin|vmax)(!important)?$/;
      const colspan = c.attributes?.colspan || 1;
      const rowspan = c.attributes?.rowspan || 1;
      const calWidth: string[] = [];

      if (rowspan > 1) {
        for (let k = 1; k < rowspan; k++) {
          if (i + k < widthArray.length) {
            widthArray[i + k][currentIndex] = 'occupied';
          }
        }
      }

      for (let k = 0; k < colspan; k++) {
        const spanIndex = currentIndex + k;
        if (
          widths[spanIndex] &&
          regex.test(widths[spanIndex]) &&
          widthArray[i][currentIndex] !== 'occupied'
        ) {
          calWidth.push(widths[spanIndex]);
        }
      }

      const style: any = {};

      if (calWidth.length > 1) {
        if (calWidth.length !== colspan) {
          style.minWidth = `calc(${calWidth.join(' + ')})`;
        } else {
          style.width = `calc(${calWidth.join(' + ')})`;
        }
      } else if (!widthArray[i][currentIndex] && calWidth[0]) {
        style.width = calWidth[0];
      }

      widthArray[i][currentIndex] = style;
      widthArray[i] = widthArray[i].filter((i) => i && i !== 'occupied');

      currentIndex += colspan;
    });
  });

  return widthArray;
}

// This is duplicate with packages/utils, it will be compiled with fs in packages/utils
// fs is a node server side module, and normally browser javascript like react cannot access the
// file system

export const imageSizeRegex = /^(\d*)(px|%|vw|vh|rem|em|vmin|vmax)$/;

function validateImageStyle(style: Record<string, string>) {
  const supportedStyle = ['width', 'height', 'scale', 'display'];
  const supportedDisplayStyle = ['inline', 'block', 'inline-block'];
  // check if there is any unsupported style
  Object.keys(style).forEach((key) => {
    if (!supportedStyle.includes(key)) {
      throw new Error(`invalid or unsupported style ${key}=${style[key]}`);
    }
    if (['width', 'height'].includes(key) && !imageSizeRegex.test(style[key])) {
      throw new Error(`invalid image size: ${key}=${style[key]}`);
    }
    if (key === 'scale' && Number.isNaN(+style.scale)) {
      throw new Error(`invalid image scale: ${key}=${style[key]}`);
    }
    if (key === 'display' && !supportedDisplayStyle.includes(style[key])) {
      throw new Error(
        `invalid or unsupported image style ${key}=${style[key]}, supported: ${supportedDisplayStyle.join(', ')}`,
      );
    }
  });
  return true;
}

function generateImageStyle(style: Record<string, string>) {
  if (!style || !validateImageStyle(style)) {
    return {};
  }

  const { scale, ...rest } = style;
  const reactStyle: Record<string, string | number> = rest;
  if (scale) {
    reactStyle.transform = `scale(${style.scale})`;
  }
  if (['inline', 'inline-block'].includes('' + reactStyle.display)) {
    reactStyle.margin = 0;
  }
  return reactStyle;
}

function resolveAlt(content: SDM.Content.Image): string {
  if (content.alt) {
    return content.alt;
  }

  const imageName: string = path.parse(content.url).name;

  if (!imageName.includes('-')) {
    return imageName;
  }

  return imageName
    .split('-')
    .map((item) => {
      return item.charAt(0).toUpperCase() + item.slice(1);
    })
    .join(' ');
}

function checkIsOpenedMenu(menu, pathname: string) {
  // Find the first non-empty string in the pathname, prevent empty when pathname has a trailing slash
  const parts = pathname.split('/');
  let currentPage = '';
  while (parts.length && !currentPage) {
    currentPage = parts.pop() as string;
  }
  const [basePath] = pathname?.split('#') ?? [];

  if (
    (menu.url?.includes('#') && currentPage === menu.url) ||
    menu.path?.replace(/\/+$/, '') === pathname?.replace(/\/+$/, '')
  ) {
    return true;
  }

  if (
    (pathname?.includes('#') && menu.url === basePath) ||
    menu.path?.replace(/\/+$/, '') === basePath?.replace(/\/+$/, '')
  ) {
    return true;
  }

  if (menu.children) {
    for (const child of menu.children) {
      const found = checkIsOpenedMenu(child, pathname);
      if (found) {
        return found;
      }
    }
  }

  return false;
}

function checkIsRootMenu(menu, menuRoot) {
  return (
    menu.parent &&
    menu.parent.type === 'docSpaceMenu' &&
    menuRoot === menu.docLeafId
  );
}

function findObjectByPath(currentPath: string, object: Record<string, any>) {
  // Normalize both url before compare by removing all trailing slashes
  if (object.path?.replace(/\/+$/, '') === currentPath?.replace(/\/+$/, '')) {
    return object;
  }
  if (object.children) {
    for (const child of object.children) {
      const found = findObjectByPath(currentPath, child);
      if (found) {
        return found;
      }
    }
  }
  return null;
}

export {
  _exhaustiveCheck,
  resolveStyle,
  resolveInlineStyle,
  resolveAlt,
  generateImageStyle,
  checkIsOpenedMenu,
  checkIsRootMenu,
  findObjectByPath,
  mapCellWidth,
};
